import { FC, useEffect, useState } from "react";
import { Home, Info, ChevronRight } from "lucide-react";

import {
  CategoryTreeObject,
  LinkedCategory,
} from "@vviinn/user-managment-client-ts";

import { CategoryItem } from "./CategoryItem";
import { MEDIUM_ICON_SIZE_STYLES } from "../../../../../ui/styles";
import { traverseCategoryTree } from "../../../../category/helpers/traverseCategoryTree";
import { Button } from "../../../../../ui/layout/molecules/Button";
import {
  BORDER_STYLES,
  DIVIDED_STYLES,
  ROW_SUBTITLE_STYLES,
} from "../../../../../ui/layout/detailedPages/styles";
import { Styled } from "../../../../../ui/layout";

type CategoryTreeProps = Styled & {
  tree?: CategoryTreeObject;
  assignedCategories?: LinkedCategory[];
  onAssign: (arg: CategoryTreeObject) => void;
  onUnassign: (arg: CategoryTreeObject) => void;
};

const findByTitle = (
  title: string,
  tree: CategoryTreeObject,
): [CategoryTreeObject, CategoryTreeObject[]] | undefined =>
  traverseCategoryTree((c) => c.title === title, tree);

const BREADCRUMBS_STYLES = `h-5 flex items-center text-sm hover:text-stone-700 dark:hover:text-stone-300 mr-0.5 mt-2 ${ROW_SUBTITLE_STYLES}`;

export const CategoryTree: FC<React.PropsWithChildren<CategoryTreeProps>> = ({
  tree,
  assignedCategories,
  onAssign,
  onUnassign,
  className = "",
}) => {
  const [selectedCategory, setSelectedCategory] = useState(tree);
  const [breadcrumbs, setBreadcrumbs] = useState([
    selectedCategory?.title ?? "",
  ]);

  useEffect(() => {
    setSelectedCategory(tree);
    setBreadcrumbs([tree?.title ?? ""]);
  }, [tree]);

  const selectCategory = (category: CategoryTreeObject) => {
    setBreadcrumbs([...breadcrumbs, category?.title ?? ""]);
    setSelectedCategory(category);
  };

  const changeBreadcrumbs = (activeTitleIndex: number) => {
    setBreadcrumbs(breadcrumbs.filter((_, i) => i <= activeTitleIndex));
  };

  const handleBreadcrumbClick = (title: string) => {
    if (!tree) return;
    const category = findByTitle(title, tree);
    const activeTitleIndex = breadcrumbs.findIndex((b) => b === title);
    setSelectedCategory(category?.[0]);
    changeBreadcrumbs(activeTitleIndex);
  };

  const resetBreadcrumbTree = () => {
    setSelectedCategory(undefined);
    changeBreadcrumbs(-1);
  };

  const isAssigned = (c: CategoryTreeObject) => {
    if (!assignedCategories) return false;
    return assignedCategories.map((l) => l.code).includes(c.categoryCode);
  };

  if (Object.keys(tree ?? {}).length === 0) {
    return (
      <div className="mt-3 flex rounded bg-stone-100 p-3 text-base text-stone-700 dark:bg-stone-600 dark:text-white">
        <Info className="mr-2.5 h-10 w-10" /> Shop feed categories are not
        available. Please add shop feed first.
      </div>
    );
  }

  return (
    <aside className={`flex flex-col gap-2.5 ${className}`}>
      <div className="breadcrumbs flex flex-wrap items-center">
        <Button
          title="home"
          onClick={resetBreadcrumbTree}
          buttonType="iconOnly"
          className={BREADCRUMBS_STYLES}
        >
          <Home className={MEDIUM_ICON_SIZE_STYLES} />
        </Button>
        {breadcrumbs.map((breadcrumb) => (
          <Button
            title={breadcrumb}
            key={breadcrumb}
            buttonType="custom"
            className={`${BREADCRUMBS_STYLES} [&>span]:rounded-lg [&>span]:bg-stone-200 [&>span]:px-2 [&>span]:py-1 [&>span]:text-xs [&>span]:text-stone-600 [&>span]:dark:bg-stone-700 [&>span]:dark:text-stone-300`}
            onClick={() => handleBreadcrumbClick(breadcrumb)}
            textStyle="show"
          >
            <ChevronRight className={`mr-0.5 ${MEDIUM_ICON_SIZE_STYLES}`} />
          </Button>
        ))}
      </div>

      <div className={`subcategories ${DIVIDED_STYLES} ${BORDER_STYLES}`}>
        {(selectedCategory
          ? (selectedCategory?.tree ?? [])
          : [tree as CategoryTreeObject]
        ).map((c) => (
          <CategoryItem
            category={c}
            key={c.categoryCode}
            isAssigned={isAssigned(c)}
            onLinkClick={() => onAssign(c)}
            onUnlinkClick={() => onUnassign(c)}
            onCategoryClick={() =>
              c.tree.length > 0 ? selectCategory(c) : () => {}
            }
          />
        ))}
      </div>
    </aside>
  );
};
